// ═══════════════════════════════════════════════════════════════
// v3.3.824 — عروض الأراضي (نظام التسويق)
// خريطة MapLibre GL بطبقة أقمار صناعية (Mapbox Satellite إن وُجد مفتاح، وإلا
// Esri World Imagery بلا مفتاح) — الأراضي المعروضة كدبابيس + حدود اختيارية
// (رسم مضلع بالنقر)، وبطاقة تفاصيل لكل أرض: المساحة، عدد الشوارع، عرض الشوارع،
// سعر المتر، السعر الإجمالي، المالك/الوسيط، الحالة، ملاحظات.
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
// v3.3.833 — CRA 5 يعيد تصغير كود الـworker المضمّن في maplibre-gl فينهار ("a is not defined")
// → استخدام نسخة CSP مع worker صريح (الحل الرسمي لبيئات التجميع)
import maplibregl from "maplibre-gl/dist/maplibre-gl-csp";
import "maplibre-gl/dist/maplibre-gl.css";
import { landOffers as api } from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiMapPin, FiX, FiCrosshair, FiSettings, FiLayers } from "react-icons/fi";

// v3.3.834 — worker خارجي بمسار مطلق (ملف ثابت في public) بدل المضمّن الذي يكسره تصغير CRA
maplibregl.setWorkerUrl(window.location.origin + "/maplibre-gl-csp-worker.js"); // v4: setWorkerUrl() هي الدالة الصحيحة (الإسناد المباشر لا يعمل → كان يحمّل الصفحة كـworker)
// v3.3.835/836 — تشكيل النص العربي في أسماء الأحياء: إضافة RTL من CDN (النسخة المحلية فشلت بـ importScripts
// داخل الـworker وأوقفت الرسم). تُحمَّل كسولًا عند أول نص عربي؛ وفشلها لا يوقف الطبقات.
try {
  if (maplibregl.getRTLTextPluginStatus && maplibregl.getRTLTextPluginStatus() === "unavailable") {
    maplibregl.setRTLTextPlugin("https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.2.3/mapbox-gl-rtl-text.js",
      (err) => { if (err) console.warn("[rtl] plugin failed:", err && err.message); }, true);
  }
} catch (e) { /* تجاهل */ }

// v3.3.835 — عند النقر على حي: تكبير على حدوده
// v3.3.837 — تكبير على حي (feature) + إبراز حدوده — مشترك بين النقر على الخريطة وقائمة الأحياء
export function zoomToDistrict(map, f) {
  if (!map || !f || !f.geometry) return;
  const coords = f.geometry.type === "Polygon" ? f.geometry.coordinates.flat() : f.geometry.coordinates.flat(2);
  let minX = 180, minY = 90, maxX = -180, maxY = -90;
  for (const [x, y] of coords) { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y; }
  map.fitBounds([[minX, minY], [maxX, maxY]], { padding: 60, duration: 900, maxZoom: 16 });
  const pid = f.properties?.id;
  try { map.setPaintProperty("districts-line", "line-width", ["case", ["==", ["get", "id"], pid], 4, 1.5]); map.setPaintProperty("districts-line", "line-color", ["case", ["==", ["get", "id"], pid], "#fde047", "#ffffff"]); } catch (_) {}
}
export function districtName(f) { const p = f?.properties || {}; return p.name_ar || p.name || p.name_en || `حي ${p.id ?? ""}`; }

// v3.3.839 — تركيز حي واحد: حدود + اسم فقط (بلا تعبئة) — مستقل تمامًا عن طبقة تظليل الأحياء
export function showDistrictFocus(map, f, opts = {}) {
  if (!map || !f || !f.geometry) return;
  map.__focusFeature = f; // لإعادة الرسم بعد تبديل الستايل
  const data = { type: "FeatureCollection", features: [{ ...f, properties: { ...f.properties, name: districtName(f) } }] };
  const add = () => {
    if (!map.getSource("district-focus")) map.addSource("district-focus", { type: "geojson", data });
    else map.getSource("district-focus").setData(data);
    if (!map.getLayer("district-focus-line"))
      map.addLayer({ id: "district-focus-line", type: "line", source: "district-focus",
        paint: { "line-color": "#fde047", "line-width": 3.5, "line-opacity": 0.95 } });
    if (!map.getLayer("district-focus-label")) {
      try {
        map.addLayer({ id: "district-focus-label", type: "symbol", source: "district-focus",
          layout: { "text-field": ["get", "name"], "text-font": ["Noto Sans Regular"], "text-size": 15, "text-allow-overlap": true },
          paint: { "text-color": "#ffffff", "text-halo-color": "#0f172a", "text-halo-width": 1.8 } });
      } catch (_) {}
    }
  };
  try { add(); } catch (_) { map.once("styledata", () => { try { add(); } catch (_) {} }); }
  if (opts.fit !== false) {
    const coords = f.geometry.type === "Polygon" ? f.geometry.coordinates.flat() : f.geometry.coordinates.flat(2);
    let minX = 180, minY = 90, maxX = -180, maxY = -90;
    for (const [x, y] of coords) { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y; }
    map.fitBounds([[minX, minY], [maxX, maxY]], { padding: 60, duration: 900, maxZoom: 16 });
  }
}
export function clearDistrictFocus(map) {
  if (!map) return;
  map.__focusFeature = null;
  try {
    ["district-focus-line", "district-focus-label"].forEach(id => map.getLayer(id) && map.removeLayer(id));
    if (map.getSource("district-focus")) map.removeSource("district-focus");
  } catch (_) {}
}

// v3.3.839 — قائمة أحياء مخصصة: مفضلة (+/-) أولًا ثم الكل + بحث؛ الاختيار يعرض الحدود والاسم فقط
const FAVS_KEY = "rael_district_favs";
const readFavs = () => { try { return JSON.parse(localStorage.getItem(FAVS_KEY)) || []; } catch { return []; } };
export function DistrictPicker({ map, geojson, onEnsureData }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [favs, setFavs] = useState(readFavs);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(geojson);
  const [selName, setSelName] = useState(null);
  const boxRef = useRef(null);
  useEffect(() => { if (geojson) setData(geojson); }, [geojson]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const toggleOpen = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (!data && onEnsureData) {
      setBusy(true);
      try { const d = await onEnsureData(); setData(d); } catch { /* التنبيه في الصفحة */ }
      setBusy(false);
    }
  };
  const toggleFav = (name, e) => {
    e.stopPropagation();
    setFavs(prev => {
      const next = prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name];
      try { localStorage.setItem(FAVS_KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  };
  const pick = (f) => { showDistrictFocus(map, f); setSelName(districtName(f)); setOpen(false); setQ(""); };
  const clearSel = (e) => { e.stopPropagation(); clearDistrictFocus(map); setSelName(null); };
  const all = (data?.features || []).slice().sort((a, b) => districtName(a).localeCompare(districtName(b), "ar"));
  const qt = q.trim();
  const match = (f) => !qt || districtName(f).includes(qt);
  const favFeats = all.filter(f => favs.includes(districtName(f)) && match(f));
  const restFeats = all.filter(f => !favs.includes(districtName(f)) && match(f));
  const row = (f) => {
    const name = districtName(f);
    const isFav = favs.includes(name);
    return (
      <div key={name} onClick={() => pick(f)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "7px 10px",
          cursor: "pointer", fontSize: 12.5, color: "#334155", borderRadius: 6 }}
        onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
        <span style={{ fontWeight: selName === name ? 800 : 600, color: selName === name ? "#0f766e" : undefined,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
        <button onClick={(e) => toggleFav(name, e)} title={isFav ? "إزالة من المفضلة" : "إضافة للمفضلة"}
          style={{ border: "1px solid " + (isFav ? "#fecaca" : "#e2e8f0"), background: isFav ? "#fff1f2" : "#f8fafc",
            color: isFav ? "#dc2626" : "#0f766e", borderRadius: 6, width: 22, height: 22, lineHeight: "18px",
            fontSize: 14, fontWeight: 800, cursor: "pointer", flexShrink: 0, padding: 0 }}>
          {isFav ? "−" : "+"}
        </button>
      </div>
    );
  };
  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <button onClick={toggleOpen} title="اختر حيًا — يظهر اسمه وحدوده مع تكبير عليه"
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: selName ? "#0f766e" : "#fff",
          color: selName ? "#fff" : "#334155", border: "1px solid " + (selName ? "#0f766e" : "#e2e8f0"), borderRadius: 8,
          fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", maxWidth: 220 }}>
        🔍 <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selName || "اختر حيًا…"}</span>
        {selName && <span onClick={clearSel} title="إلغاء التحديد"
          style={{ background: "rgba(255,255,255,0.25)", borderRadius: 5, width: 18, height: 18, lineHeight: "18px",
            textAlign: "center", fontSize: 11, flexShrink: 0 }}>✕</span>}
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 30, width: 250, maxHeight: 380,
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: 8, borderBottom: "1px solid #f1f5f9" }}>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="ابحث عن حي…"
              style={{ width: "100%", padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 12.5,
                fontFamily: "inherit", boxSizing: "border-box", outline: "none" }} />
          </div>
          <div style={{ overflowY: "auto", padding: 6, flex: 1 }}>
            {busy && <div style={{ padding: 14, textAlign: "center", fontSize: 12, color: "#94a3b8" }}>جارٍ جلب الأحياء…</div>}
            {!busy && !all.length && <div style={{ padding: 14, textAlign: "center", fontSize: 12, color: "#94a3b8" }}>لا توجد بيانات أحياء</div>}
            {favFeats.length > 0 && (
              <>
                <div style={{ fontSize: 10.5, fontWeight: 800, color: "#b45309", padding: "4px 10px 2px" }}>⭐ المفضلة</div>
                {favFeats.map(row)}
                {restFeats.length > 0 && <div style={{ borderTop: "1px dashed #e2e8f0", margin: "6px 4px" }} />}
              </>
            )}
            {restFeats.length > 0 && (
              <>
                <div style={{ fontSize: 10.5, fontWeight: 800, color: "#64748b", padding: "4px 10px 2px" }}>كل الأحياء</div>
                {restFeats.map(row)}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
export function bindDistrictClick(map) {
  if (!map || map.__districtClickBound) return;
  map.__districtClickBound = true;
  const onClick = (e) => {
    if (!map.getLayer("districts-fill") || map.__pickingMode) return; // لا تكبير أثناء وضع التحديد
    const feats = map.queryRenderedFeatures(e.point, { layers: ["districts-fill"] });
    if (!feats.length) return;
    zoomToDistrict(map, feats[0]);
  };
  map.on("click", onClick);
  map.on("mousemove", (e) => { if (!map.getLayer("districts-fill")) return; const h = map.queryRenderedFeatures(e.point, { layers: ["districts-fill"] }).length > 0; if (h && !map.__pickingMode) map.getCanvas().style.cursor = "pointer"; });
}

const STATUSES = ["معروضة", "محجوزة", "مباعة", "موقوفة"];
const STATUS_COLORS = {
  "معروضة": { bg: "#dcfce7", color: "#166534", pin: "#16a34a" },
  "محجوزة": { bg: "#fef3c7", color: "#92400e", pin: "#f59e0b" },
  "مباعة":  { bg: "#fee2e2", color: "#991b1b", pin: "#dc2626" },
  "موقوفة": { bg: "#e2e8f0", color: "#475569", pin: "#64748b" },
};
const LAND_TYPES = ["سكني", "تجاري", "سكني تجاري", "زراعي", "صناعي", "استثماري", "أخرى"];
const fmt = (n) => (n == null || n === "" || isNaN(Number(n))) ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 });
const inp = { width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" };
const lbl = { fontSize: 10, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 };

// v3.3.830 — جدة كمركز افتراضي (كانت الرياض)
export const DEFAULT_CENTER = [39.1925, 21.4858];
export const DEFAULT_ZOOM = 11;

// v3.3.830 — طبقة أحياء المدينة (OpenStreetMap عبر الخادم، مخزّنة مؤقتًا): إضافة/إزالة على الخريطة
const DISTRICT_PALETTE = ["#f59e0b", "#14b8a6", "#8b5cf6", "#ec4899", "#3b82f6", "#84cc16", "#f97316", "#06b6d4"];
export function applyDistrictsLayer(map, geojson) {
  if (!map) return;
  const has = !!map.getSource("districts");
  if (!geojson) {
    if (has) { ["districts-fill", "districts-line", "districts-label"].forEach(id => map.getLayer(id) && map.removeLayer(id)); map.removeSource("districts"); }
    return;
  }
  // لون ثابت لكل حي (حسب ترتيبه) — محسوب هنا لا في تعبير الستايل
  const colored = { type: "FeatureCollection", features: (geojson.features || []).map((f, i) => ({ ...f, properties: { ...f.properties, color: DISTRICT_PALETTE[i % DISTRICT_PALETTE.length] } })) };
  const addLayers = () => {
    if (!map.getSource("districts")) map.addSource("districts", { type: "geojson", data: colored });
    // v3.3.832 — فوق طبقات الصور مباشرة (قبل land-polys إن وُجدت وإلا في الأعلى)؛ تعبئة أوضح + خط أبيض
    const before = map.getLayer("land-polys-fill") ? "land-polys-fill" : undefined;
    if (!map.getLayer("districts-fill")) map.addLayer({ id: "districts-fill", type: "fill", source: "districts", paint: { "fill-color": ["get", "color"], "fill-opacity": 0.35 } }, before);
    if (!map.getLayer("districts-line")) map.addLayer({ id: "districts-line", type: "line", source: "districts", paint: { "line-color": "#ffffff", "line-width": 1.5, "line-opacity": 0.95 } }, before);
    if (!map.getLayer("districts-label")) {
      try {
        map.addLayer({ id: "districts-label", type: "symbol", source: "districts",
          layout: { "text-field": ["get", "name"], "text-font": ["Noto Sans Regular"], "text-size": 12, "text-allow-overlap": false, "text-padding": 4 },
          paint: { "text-color": "#ffffff", "text-halo-color": "#0f172a", "text-halo-width": 1.4 } }, before);
      } catch (e) { console.warn("[districts] label layer skipped:", e.message); }
    }
    map.triggerRepaint();
    window.__rmap = map; // تشخيص
    bindDistrictClick(map); // v3.3.835
    const order = map.getStyle().layers.map(l => l.id).join(" > ");
    console.info("[districts] layers:", ["districts-fill", "districts-line", "districts-label"].filter(id => map.getLayer(id)).join(","), "features:", colored.features.length, "| order:", order);
    map.once("idle", () => {
      try {
        const n = map.queryRenderedFeatures({ layers: ["districts-fill"] }).length;
        const src = map.getSource("districts");
        console.info("[districts] rendered features in view:", n, "| source loaded:", map.isSourceLoaded("districts"), "| zoom:", map.getZoom().toFixed(1), "| center:", map.getCenter());
        if (n === 0) console.warn("[districts] 0 rendered — source data features:", (src && src._data && src._data.features && src._data.features.length));
      } catch (e) { console.warn("[districts] diag:", e.message); }
    });
  };
  if (!has) {
    try { addLayers(); }
    catch (e) { console.warn("[districts] add failed, retry on styledata:", e.message); map.once("styledata", () => { try { addLayers(); } catch (e2) { console.error("[districts]", e2.message); } }); }
  } else {
    map.getSource("districts").setData(colored);
    addLayers();
  }
}

// ─── ستايل الخريطة: أقمار صناعية (Mapbox إن وُجد مفتاح، وإلا Esri) + أسماء OSM خفيفة ───
export function buildStyle(mapboxToken) {
  const sat = mapboxToken
    ? { type: "raster", tiles: [`https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}@2x.jpg90?access_token=${mapboxToken}`], tileSize: 512, maxzoom: 22, attribution: "© Mapbox © Maxar" }
    : { type: "raster", tiles: ["https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"], tileSize: 256, maxzoom: 19, attribution: "Esri, Maxar, Earthstar Geographics" };
  return {
    version: 8,
    glyphs: window.location.origin + "/fonts/{fontstack}/{range}.pbf",
    sources: {
      satellite: sat,
      // طبقة حدود/أسماء شفافة فوق الأقمار (Esri Reference) — تساعد على التوجه
      labels: { type: "raster", tiles: ["https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"], tileSize: 256, maxzoom: 19 },
    },
    layers: [
      { id: "satellite", type: "raster", source: "satellite" },
      { id: "labels", type: "raster", source: "labels", paint: { "raster-opacity": 0.9 } },
    ],
  };
}

// مساحة مضلع تقريبية بالمتر المربع (Shoelace على إسقاط مستوٍ محلي)
function polygonAreaSqm(coords) {
  if (!coords || coords.length < 3) return 0;
  const R = 6378137;
  const lat0 = coords.reduce((s, c) => s + c[1], 0) / coords.length * Math.PI / 180;
  const pts = coords.map(([lng, lat]) => [R * (lng * Math.PI / 180) * Math.cos(lat0), R * (lat * Math.PI / 180)]);
  let a = 0;
  for (let i = 0; i < pts.length; i++) { const [x1, y1] = pts[i]; const [x2, y2] = pts[(i + 1) % pts.length]; a += x1 * y2 - x2 * y1; }
  return Math.abs(a) / 2;
}

export default function LandOffersPage() {
  const { user } = useAuth();
  const [lands, setLands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canManage, setCanManage] = useState(false);
  const [cfg, setCfg] = useState({ provider: "esri", mapbox_token: null });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);   // land id
  const [modal, setModal] = useState(null);         // null | {} | land | {prefill:true,...} من اللصق الذكي
  const [smartOpen, setSmartOpen] = useState(false); // v3.3.952
  const [showSettings, setShowSettings] = useState(false);
  const [picking, setPicking] = useState(null);     // {mode:'point'|'polygon', cb}
  const [drawPts, setDrawPts] = useState([]);
  const [baseLayer, setBaseLayer] = useState("satellite"); // satellite | streets
  const mapRef = useRef(null);
  const mapEl = useRef(null);
  const markersRef = useRef(new Map());
  const isAdmin = (user?.role === "admin" && !user?.impersonating) || user?.is_admin;
  // v3.3.830 — طبقة أحياء جدة (OSM)
  const [showDistricts, setShowDistricts] = useState(false);
  const [districts, setDistricts] = useState(null);
  const [districtsBusy, setDistrictsBusy] = useState(false);
  // v3.3.839 — جلب بيانات الأحياء عند الحاجة (مشترك بين زر التظليل وقائمة الأحياء المستقلة)
  const ensureDistricts = async () => {
    if (districts) return districts;
    try { const r = await api.districts("jeddah"); setDistricts(r.data); return r.data; }
    catch (e) { toast.error(e.response?.data?.error || "تعذر جلب الأحياء"); throw e; }
  };
  const toggleDistricts = async () => {
    if (showDistricts) { setShowDistricts(false); applyDistrictsLayer(mapRef.current, null); return; }
    setDistrictsBusy(true);
    try { const d = await ensureDistricts(); applyDistrictsLayer(mapRef.current, d); setShowDistricts(true); }
    catch (_) { /* نُبّه في ensureDistricts */ }
    setDistrictsBusy(false);
  };

  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([api.list(), api.mapConfig()]);
      setLands(r.data?.lands || []);
      setCanManage(!!r.data?.can_manage);
      setCfg(c.data || {});
    } catch { toast.error("فشل تحميل عروض الأراضي"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // ─── إنشاء الخريطة ───
  useEffect(() => {
    if (!mapEl.current || mapRef.current || loading) return;
    const map = new maplibregl.Map({
      container: mapEl.current,
      style: buildStyle(cfg.mapbox_token),
      center: DEFAULT_CENTER, zoom: 11, attributionControl: true,
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: false }), "top-left");
    map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-left");
    map.addControl(new maplibregl.GeolocateControl({ positionOptions: { enableHighAccuracy: true }, trackUserLocation: false }), "top-left");
    map.on("load", () => {
      map.addSource("land-polys", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({ id: "land-polys-fill", type: "fill", source: "land-polys", paint: { "fill-color": ["get", "color"], "fill-opacity": 0.25 } });
      map.addLayer({ id: "land-polys-line", type: "line", source: "land-polys", paint: { "line-color": ["get", "color"], "line-width": 2.5 } });
      map.addSource("draw", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({ id: "draw-fill", type: "fill", source: "draw", paint: { "fill-color": "#f59e0b", "fill-opacity": 0.25 } });
      map.addLayer({ id: "draw-line", type: "line", source: "draw", paint: { "line-color": "#f59e0b", "line-width": 2, "line-dasharray": [2, 1] } });
      map.addLayer({ id: "draw-pts", type: "circle", source: "draw", filter: ["==", "$type", "Point"], paint: { "circle-radius": 5, "circle-color": "#fff", "circle-stroke-color": "#f59e0b", "circle-stroke-width": 2 } });
      mapRef.current = map;
      setTimeout(() => map.resize(), 50);
      renderLands();
    });
    return () => { map.remove(); mapRef.current = null; };
  }, [loading]);

  // تبديل مزوّد الأقمار عند تغيّر المفتاح/الطبقة
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    const style = baseLayer === "streets"
      ? { version: 8, glyphs: window.location.origin + "/fonts/{fontstack}/{range}.pbf",
          sources: { osm: { type: "raster", tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"], tileSize: 256, maxzoom: 19, attribution: "© OpenStreetMap" } },
          layers: [{ id: "osm", type: "raster", source: "osm" }] }
      : buildStyle(cfg.mapbox_token);
    map.setStyle(style);
    map.once("styledata", () => {
      if (!map.getSource("land-polys")) {
        map.addSource("land-polys", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
        map.addLayer({ id: "land-polys-fill", type: "fill", source: "land-polys", paint: { "fill-color": ["get", "color"], "fill-opacity": 0.25 } });
        map.addLayer({ id: "land-polys-line", type: "line", source: "land-polys", paint: { "line-color": ["get", "color"], "line-width": 2.5 } });
        map.addSource("draw", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
        map.addLayer({ id: "draw-fill", type: "fill", source: "draw", paint: { "fill-color": "#f59e0b", "fill-opacity": 0.25 } });
        map.addLayer({ id: "draw-line", type: "line", source: "draw", paint: { "line-color": "#f59e0b", "line-width": 2, "line-dasharray": [2, 1] } });
        map.addLayer({ id: "draw-pts", type: "circle", source: "draw", filter: ["==", "$type", "Point"], paint: { "circle-radius": 5, "circle-color": "#fff", "circle-stroke-color": "#f59e0b", "circle-stroke-width": 2 } });
      }
      renderLands();
      if (showDistricts && districts) applyDistrictsLayer(map, districts); // v3.3.830 — إعادة طبقة الأحياء بعد تبديل الستايل
      if (map.__focusFeature) showDistrictFocus(map, map.__focusFeature, { fit: false }); // v3.3.839 — إعادة حدود الحي المركّز
    });
  }, [cfg.mapbox_token, baseLayer]); // eslint: showDistricts/districts مقصود عدم إدراجهما

  // ─── الفلترة ───
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lands.filter(l => {
      if (statusFilter && l.status !== statusFilter) return false;
      if (q && !([l.title, l.city, l.district, l.plan_number, l.plot_number, l.owner_name, l.broker_name, l.land_type].filter(Boolean).join(" ").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [lands, search, statusFilter]);

  // ─── رسم الدبابيس والمضلعات ───
  const renderLands = useCallback(() => {
    const map = mapRef.current; if (!map) return;
    // markers
    markersRef.current.forEach(m => m.remove()); markersRef.current.clear();
    filtered.forEach(l => {
      const c = STATUS_COLORS[l.status] || STATUS_COLORS["معروضة"];
      const el = document.createElement("div");
      el.style.cssText = `width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${c.pin};border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.4);cursor:pointer;display:flex;align-items:center;justify-content:center;${selected === l.id ? "outline:3px solid #fff;outline-offset:1px;" : ""}`;
      const inner = document.createElement("div");
      inner.style.cssText = "transform:rotate(45deg);color:#fff;font-size:11px;font-weight:800;font-family:inherit";
      inner.textContent = l.area_sqm ? (Number(l.area_sqm) >= 1000 ? `${Math.round(Number(l.area_sqm) / 1000)}k` : String(Math.round(Number(l.area_sqm)))) : "";
      el.appendChild(inner);
      el.title = `${l.title} — ${l.status}`;
      el.addEventListener("click", (e) => { e.stopPropagation(); setSelected(l.id); });
      const mk = new maplibregl.Marker({ element: el, anchor: "bottom" }).setLngLat([l.lng, l.lat]).addTo(map);
      markersRef.current.set(l.id, mk);
    });
    // polygons
    const src = map.getSource("land-polys");
    if (src) src.setData({ type: "FeatureCollection", features: filtered.filter(l => l.polygon?.type === "Polygon").map(l => ({ type: "Feature", properties: { id: l.id, color: (STATUS_COLORS[l.status] || STATUS_COLORS["معروضة"]).pin }, geometry: l.polygon })) });
  }, [filtered, selected]);
  useEffect(() => { renderLands(); }, [renderLands]);

  // ─── النقر على الخريطة: وضع التقاط النقطة/المضلع ───
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    const onClick = (e) => {
      if (!picking) return;
      const { lng, lat } = e.lngLat;
      if (picking.mode === "point") { picking.cb({ lat, lng }); setPicking(null); return; }
      if (picking.mode === "polygon") setDrawPts(p => [...p, [lng, lat]]);
    };
    const onDbl = (e) => { if (picking?.mode === "polygon") { e.preventDefault(); finishPolygon(); } };
    map.on("click", onClick); map.on("dblclick", onDbl);
    map.__pickingMode = !!picking; map.getCanvas().style.cursor = picking ? "crosshair" : "";
    return () => { map.off("click", onClick); map.off("dblclick", onDbl); if (map.getCanvas()) map.getCanvas().style.cursor = ""; };
  }, [picking, drawPts]);

  // تحديث طبقة الرسم
  useEffect(() => {
    const map = mapRef.current; if (!map || !map.getSource("draw")) return;
    const feats = drawPts.map(p => ({ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: p } }));
    if (drawPts.length >= 2) feats.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: drawPts } });
    if (drawPts.length >= 3) feats.push({ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [[...drawPts, drawPts[0]]] } });
    map.getSource("draw").setData({ type: "FeatureCollection", features: feats });
  }, [drawPts]);

  const finishPolygon = () => {
    if (drawPts.length < 3) { toast.error("حدد 3 نقاط على الأقل"); return; }
    const ring = [...drawPts, drawPts[0]];
    const poly = { type: "Polygon", coordinates: [ring] };
    const area = polygonAreaSqm(drawPts);
    const lng = drawPts.reduce((s, p) => s + p[0], 0) / drawPts.length, lat = drawPts.reduce((s, p) => s + p[1], 0) / drawPts.length;
    picking?.cb({ polygon: poly, area_sqm: Math.round(area), lat, lng });
    setPicking(null); setDrawPts([]);
    const map = mapRef.current; if (map?.getSource("draw")) map.getSource("draw").setData({ type: "FeatureCollection", features: [] });
  };
  const cancelPick = () => { setPicking(null); setDrawPts([]); const map = mapRef.current; if (map?.getSource("draw")) map.getSource("draw").setData({ type: "FeatureCollection", features: [] }); };

  const flyTo = (l) => { const map = mapRef.current; if (map && l) map.flyTo({ center: [l.lng, l.lat], zoom: Math.max(map.getZoom(), 16), speed: 1.2 }); };
  useEffect(() => { const l = lands.find(x => x.id === selected); if (l) flyTo(l); }, [selected]);

  const remove = async (l) => {
    if (!window.confirm(`حذف الأرض "${l.title}"؟`)) return;
    try { await api.remove(l.id); toast.success("تم الحذف"); setSelected(null); load(); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الحذف"); }
  };
  const quickStatus = async (l, status) => {
    try { await api.setStatus(l.id, status); toast.success("حُدّثت الحالة"); load(); }
    catch (e) { toast.error(e.response?.data?.error || "فشل"); }
  };

  const sel = lands.find(l => l.id === selected) || null;
  const counts = STATUSES.reduce((o, s) => ({ ...o, [s]: lands.filter(l => l.status === s).length }), {});

  return (
    <div dir="rtl" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 40px)", padding: "14px 16px 10px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}><FiMapPin /> عروض الأراضي</h2>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
            {lands.length} أرض • {STATUSES.map(s => `${s}: ${counts[s]}`).join(" • ")} • الخريطة: {cfg.provider === "mapbox" ? "Mapbox Satellite" : "Esri World Imagery"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => setBaseLayer(b => b === "satellite" ? "streets" : "satellite")} title="تبديل الطبقة"
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: "#fff", color: "#334155", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            <FiLayers /> {baseLayer === "satellite" ? "خريطة شوارع" : "أقمار صناعية"}
          </button>
          {/* v3.3.830 — أحياء جدة (OpenStreetMap) */}
          <button onClick={toggleDistricts} disabled={districtsBusy} title="تظليل أحياء جدة (مصدر مفتوح: OpenStreetMap)"
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: showDistricts ? "#0f766e" : "#fff", color: showDistricts ? "#fff" : "#334155", border: "1px solid " + (showDistricts ? "#0f766e" : "#e2e8f0"), borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: districtsBusy ? "wait" : "pointer", fontFamily: "inherit" }}>
            🗺 {districtsBusy ? "جارٍ جلب الأحياء…" : showDistricts ? "إخفاء الأحياء" : "أحياء جدة"}
          </button>
          {/* v3.3.839 — قائمة الأحياء مستقلة عن زر التظليل: مفضلة + الكل، الاختيار يعرض الحدود والاسم فقط */}
          <DistrictPicker map={mapRef.current} geojson={districts} onEnsureData={ensureDistricts} />
          {isAdmin && (
            <button onClick={() => setShowSettings(true)} title="إعدادات الخريطة (مفتاح Mapbox)"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: "#fff", color: "#334155", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              <FiSettings /> إعدادات
            </button>
          )}
          {canManage && (
            <button onClick={() => setSmartOpen(true)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              ✨ لصق ذكي
            </button>
          )}{/* v3.3.952 */}
          {canManage && (
            <button onClick={() => setModal({})}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", background: "#0f766e", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              <FiPlus /> إضافة أرض
            </button>
          )}
        </div>
      </div>

      {/* Body: list | map */}
      <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 12, flex: 1, minHeight: 0 }}>
        {/* القائمة */}
        <div style={{ display: "flex", flexDirection: "column", minHeight: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>
            <div style={{ position: "relative", marginBottom: 8 }}>
              <FiSearch style={{ position: "absolute", top: 10, insetInlineStart: 10, color: "#94a3b8" }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="بحث: الاسم، المدينة، الحي، المخطط، المالك…"
                style={{ ...inp, paddingInlineStart: 32 }} />
            </div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <button onClick={() => setStatusFilter("")} style={{ padding: "4px 10px", borderRadius: 14, border: "1px solid " + (!statusFilter ? "#0f766e" : "#e2e8f0"), background: !statusFilter ? "#0f766e" : "#fff", color: !statusFilter ? "#fff" : "#475569", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>الكل ({lands.length})</button>
              {STATUSES.map(s => { const c = STATUS_COLORS[s]; const on = statusFilter === s; return (
                <button key={s} onClick={() => setStatusFilter(on ? "" : s)} style={{ padding: "4px 10px", borderRadius: 14, border: "1px solid " + (on ? c.color : "#e2e8f0"), background: on ? c.bg : "#fff", color: on ? c.color : "#475569", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{s} ({counts[s]})</button>
              ); })}
            </div>
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {loading ? <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>جاري التحميل…</div>
              : filtered.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>{lands.length ? "لا نتائج" : "لا توجد أراضٍ بعد — أضف أول أرض"}</div>
              : filtered.map(l => {
                const c = STATUS_COLORS[l.status] || STATUS_COLORS["معروضة"];
                const on = selected === l.id;
                return (
                  <div key={l.id} onClick={() => setSelected(l.id)}
                    style={{ padding: "10px 12px", borderBottom: "1px solid #f1f5f9", cursor: "pointer", background: on ? "#f0fdfa" : "#fff", borderInlineStart: on ? "4px solid #0f766e" : "4px solid transparent" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                      <div style={{ fontWeight: 800, fontSize: 13.5, color: "#0f172a" }}>{l.title}</div>
                      <span style={{ background: c.bg, color: c.color, borderRadius: 10, padding: "1px 8px", fontSize: 10, fontWeight: 800, whiteSpace: "nowrap" }}>{l.status}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{[l.city, l.district, l.land_type].filter(Boolean).join(" • ") || "—"}</div>
                    <div style={{ display: "flex", gap: 10, marginTop: 6, fontSize: 11, color: "#334155", flexWrap: "wrap" }}>
                      <span>📐 <b>{fmt(l.area_sqm)}</b> م²</span>
                      <span>🛣 <b>{l.streets_count ?? "—"}</b> شوارع{l.street_widths ? ` (${l.street_widths}م)` : ""}</span>
                      <span>💵 <b>{fmt(l.price_per_sqm)}</b> ر.س/م²</span>
                    </div>
                    {l.total_price != null && <div style={{ fontSize: 12, color: "#0f766e", fontWeight: 800, marginTop: 3 }}>الإجمالي: {fmt(l.total_price)} ر.س</div>}
                  </div>
                );
              })}
          </div>
        </div>

        {/* الخريطة */}
        <div style={{ position: "relative", minHeight: 0, borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0", background: "#0f172a" }}>
          {loading ? <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>جاري تحميل الخريطة…</div>
            : <div ref={mapEl} style={{ position: "absolute", inset: 0 }} />}

          {/* شريط وضع التقاط */}
          {picking && (
            <div style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", background: "#fffbeb", border: "1.5px solid #f59e0b", borderRadius: 10, padding: "8px 14px", zIndex: 5, display: "flex", gap: 10, alignItems: "center", boxShadow: "0 4px 14px rgba(0,0,0,0.2)", fontSize: 12.5, fontWeight: 700, color: "#92400e", direction: "rtl" }}>
              {picking.mode === "point" ? "📍 اضغط على الخريطة لتحديد موقع الأرض" : `✏️ اضغط لإضافة نقاط حدود الأرض (${drawPts.length}) — نقر مزدوج أو "إنهاء" عند الاكتمال`}
              {picking.mode === "polygon" && drawPts.length >= 3 && (
                <button onClick={finishPolygon} style={{ background: "#0f766e", color: "#fff", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", fontWeight: 700 }}>✓ إنهاء ({Math.round(polygonAreaSqm(drawPts)).toLocaleString()} م²)</button>
              )}
              {picking.mode === "polygon" && drawPts.length > 0 && (
                <button onClick={() => setDrawPts(p => p.slice(0, -1))} style={{ background: "#fff", color: "#92400e", border: "1px solid #fcd34d", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontFamily: "inherit" }}>↶ تراجع</button>
              )}
              <button onClick={cancelPick} style={{ background: "#fff", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontFamily: "inherit" }}>✕ إلغاء</button>
            </div>
          )}

          {/* بطاقة الأرض المختارة */}
          {sel && !picking && (
            <div style={{ position: "absolute", bottom: 12, right: 12, width: 360, maxWidth: "calc(100% - 24px)", background: "#fff", borderRadius: 12, boxShadow: "0 10px 30px rgba(0,0,0,0.3)", zIndex: 5, overflow: "hidden", direction: "rtl" }}>
              <div style={{ background: "linear-gradient(135deg,#134e4a,#0f766e)", color: "#fff", padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14 }}>{sel.title}</div>
                  <div style={{ fontSize: 11, opacity: 0.85 }}>{[sel.city, sel.district, sel.land_type].filter(Boolean).join(" • ")}</div>
                </div>
                <button onClick={() => setSelected(null)} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 6, width: 28, height: 28, cursor: "pointer" }}><FiX /></button>
              </div>
              <div style={{ padding: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                  {[
                    ["📐 المساحة", `${fmt(sel.area_sqm)} م²`], ["🛣 عدد الشوارع", sel.streets_count ?? "—"],
                    ["↔️ عرض الشوارع", sel.street_widths ? `${sel.street_widths} م` : "—"], ["💵 سعر المتر", `${fmt(sel.price_per_sqm)} ر.س`],
                    ["💰 الإجمالي", `${fmt(sel.total_price)} ر.س`], ["🏷 الحالة", sel.status],
                    ["🗺 المخطط / القطعة", [sel.plan_number, sel.plot_number].filter(Boolean).join(" / ") || "—"], ["📄 الصك", sel.deed_number || "—"],
                  ].map(([k, v]) => (
                    <div key={k} style={{ background: "#f8fafc", borderRadius: 8, padding: "6px 8px" }}>
                      <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700 }}>{k}</div>
                      <div style={{ fontSize: 12.5, fontWeight: 800, color: "#0f172a" }}>{v}</div>
                    </div>
                  ))}
                </div>
                {(sel.owner_name || sel.broker_name) && (
                  <div style={{ fontSize: 11.5, color: "#334155", marginBottom: 8, lineHeight: 1.7 }}>
                    {sel.owner_name && <div>👤 المالك: <b>{sel.owner_name}</b>{sel.owner_phone && <a href={`tel:${sel.owner_phone}`} style={{ marginInlineStart: 6, color: "#1d4ed8", direction: "ltr", display: "inline-block" }}>{sel.owner_phone}</a>}</div>}
                    {sel.broker_name && <div>🤝 الوسيط: <b>{sel.broker_name}</b>{sel.broker_phone && <a href={`tel:${sel.broker_phone}`} style={{ marginInlineStart: 6, color: "#1d4ed8", direction: "ltr", display: "inline-block" }}>{sel.broker_phone}</a>}</div>}
                  </div>
                )}
                {sel.notes && <div style={{ fontSize: 11.5, color: "#475569", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "6px 8px", marginBottom: 8, whiteSpace: "pre-wrap" }}>{sel.notes}</div>}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  <a href={`https://www.google.com/maps?q=${sel.lat},${sel.lng}`} target="_blank" rel="noopener noreferrer"
                    style={{ padding: "6px 10px", background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 6, fontSize: 11, fontWeight: 700, textDecoration: "none" }}>🧭 Google Maps</a>
                  <button onClick={() => { navigator.clipboard?.writeText(`${sel.lat.toFixed(7)}, ${sel.lng.toFixed(7)}`); toast.success("نُسخت الإحداثيات"); }}
                    style={{ padding: "6px 10px", background: "#f8fafc", color: "#334155", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>📋 الإحداثيات</button>
                  {canManage && (
                    <>
                      <select value={sel.status} onChange={e => quickStatus(sel, e.target.value)}
                        style={{ padding: "5px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 11, fontFamily: "inherit" }}>
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => setModal(sel)} style={{ padding: "6px 10px", background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}><FiEdit2 size={11} /> تعديل</button>
                      <button onClick={() => remove(sel)} style={{ padding: "6px 10px", background: "#fff1f2", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}><FiTrash2 size={11} /></button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {smartOpen && <SmartPasteModal onClose={() => setSmartOpen(false)} onParsed={(p) => {
        setSmartOpen(false);
        setModal({ prefill: true, ...p,
          title: p.title || "", city: p.city || "", district: p.district || "", land_type: p.land_type || "",
          total_price: p.total_price ?? "", area_sqm: p.area_sqm ?? "", streets_count: p.streets_count ?? "",
          street_widths: p.street_widths || "", price_per_sqm: p.price_per_sqm ?? "",
          owner_name: p.owner_name || "", owner_phone: p.owner_phone || "",
          broker_name: p.broker_name || "", broker_phone: p.broker_phone || "",
          plan_number: p.plan_number || "", plot_number: p.plot_number || "", deed_number: p.deed_number || "",
          notes: p.notes || "", lat: p.lat ?? "", lng: p.lng ?? "" }); // v3.3.953 — كل حقول الذكاء الاصطناعي
        if (p.lat && p.lng && mapRef.current) mapRef.current.flyTo({ center: [p.lng, p.lat], zoom: 16 });
      }} />}{/* v3.3.952 */}
      {modal && (
        <LandModal land={(modal.id || modal.prefill) ? modal : null} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }}
          startPick={(mode, cb) => { setPicking({ mode, cb }); setDrawPts([]); }} pickingActive={!!picking} mapRef={mapRef} />
      )}
      {showSettings && <MapSettingsModal cfg={cfg} onClose={() => setShowSettings(false)} onSaved={() => { setShowSettings(false); load(); }} />}
    </div>
  );
}

// ─── نافذة إضافة/تعديل أرض ───
// v3.3.952 — اللصق الذكي: الصق رسالة الواتساب → تحليل تلقائي → فتح النموذج معبأً
function SmartPasteModal({ onClose, onParsed }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  // v3.3.953 — حالة الذكاء الاصطناعي + إعداد المفتاح (أدمن)
  const [cfg, setCfg] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [keyVal, setKeyVal] = useState("");
  useEffect(() => { api.parseConfig().then(r => setCfg(r.data)).catch(() => {}); }, []);
  const saveKey = async () => {
    if (!keyVal.trim()) return toast.error("الصق المفتاح أولًا");
    try { await api.saveParseConfig({ api_key: keyVal.trim() }); toast.success("حُفظ المفتاح ✓ — التحليل الذكي مفعّل"); setKeyVal(""); setShowKey(false); api.parseConfig().then(r => setCfg(r.data)).catch(() => {}); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
  };
  const go = async () => {
    if (!text.trim()) return toast.error("الصق نص العرض أولًا");
    setBusy(true);
    try {
      const r = await api.parseOffer(text);
      const p = r.data || {};
      const got = [p.lat && "📍 الموقع", p.district && `الحي ${p.district}`, p.area_sqm && `المساحة ${p.area_sqm}م²`, p.streets_count && `${p.streets_count} شوارع`, p.total_price && `السعر ${Number(p.total_price).toLocaleString()}`, p.broker_name && `الوسيط ${p.broker_name}`].filter(Boolean);
      toast.success((p.ai_used ? "🤖 " : "") + (got.length ? `استُخرج: ${got.join(" · ")}` : "لم يُستخرج الكثير — أكمل يدويًا"));
      onParsed(p);
    } catch (e) { toast.error(e.response?.data?.error || "فشل التحليل"); }
    setBusy(false);
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1150, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div dir="rtl" onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 20, width: 560, maxWidth: "95vw" }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>✨ لصق ذكي — تحليل عرض أرض من رسالة واتساب</h3>
        <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 10, lineHeight: 1.8 }}>
          الصق الرسالة كما هي (برابط خرائط جوجل والسعر والمساحة) — النظام يفك الرابط ويحدد الموقع على الخريطة ويستخرج المساحة وعدد الشوارع والسعر واسم المرسل، ثم يفتح نموذج الإضافة معبأً للمراجعة.
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} autoFocus
          placeholder={"مثال:\n[9:39 PM] يوسف: https://maps.app.goo.gl/xxxx\n٦٥٠ شارعين\n٦ مليون ونص"}
          style={{ width: "100%", boxSizing: "border-box", padding: 10, border: "1px solid #e2e8f0", borderRadius: 10, fontSize: 12.5, fontFamily: "inherit", resize: "vertical" }} />
        <div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          {/* v3.3.953 — حالة التحليل الذكي + إعداد المفتاح للأدمن */}
          <span style={{ fontSize: 10.5, display: "inline-flex", gap: 6, alignItems: "center" }}>
            {cfg && (cfg.has_key
              ? <span style={{ background: "#dcfce7", color: "#166534", borderRadius: 10, padding: "2px 10px", fontWeight: 800 }}>🤖 التحليل الذكي مفعّل (Claude)</span>
              : <span style={{ background: "#fef3c7", color: "#92400e", borderRadius: 10, padding: "2px 10px", fontWeight: 800 }}>التحليل البسيط فقط{cfg.is_admin ? "" : " — يفعّله الأدمن"}</span>)}
            {cfg?.is_admin && <button onClick={() => setShowKey(!showKey)} style={{ padding: "2px 8px", border: "1px solid #e2e8f0", background: "#fff", color: "#7c3aed", borderRadius: 8, fontSize: 10.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>🔑 المفتاح</button>}
          </span>
          <span style={{ display: "inline-flex", gap: 8 }}>
            <button onClick={onClose} style={{ padding: "8px 16px", background: "#fff", border: "1px solid #e2e8f0", color: "#64748b", borderRadius: 8, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>إلغاء</button>
            <button onClick={go} disabled={busy} style={{ padding: "8px 20px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: 8, fontSize: 12.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>{busy ? "جارٍ التحليل…" : "🔎 تحليل وفتح النموذج"}</button>
          </span>
        </div>
        {showKey && cfg?.is_admin && (
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <input value={keyVal} onChange={(e) => setKeyVal(e.target.value)} type="password" dir="ltr"
              placeholder={cfg.has_key ? "مفتاح Anthropic API (محفوظ — الصق جديدًا للاستبدال)" : "sk-ant-... من console.anthropic.com"}
              style={{ flex: 1, padding: "8px 10px", border: "1px solid #ddd6fe", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }} />
            <button onClick={saveKey} style={{ padding: "8px 16px", background: "#0f766e", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>💾 حفظ</button>
          </div>
        )}
      </div>
    </div>
  );
}

function LandModal({ land, onClose, onSaved, startPick, pickingActive, mapRef }) {
  const [f, setF] = useState({
    title: land?.title || "", city: land?.city || "", district: land?.district || "",
    lat: land?.lat ?? "", lng: land?.lng ?? "", polygon: land?.polygon || null,
    area_sqm: land?.area_sqm ?? "", streets_count: land?.streets_count ?? "", street_widths: land?.street_widths || "",
    price_per_sqm: land?.price_per_sqm ?? "", total_price: land?.total_price ?? "",
    land_type: land?.land_type || "", owner_name: land?.owner_name || "", owner_phone: land?.owner_phone || "",
    broker_name: land?.broker_name || "", broker_phone: land?.broker_phone || "", status: land?.status || "معروضة",
    plan_number: land?.plan_number || "", plot_number: land?.plot_number || "", deed_number: land?.deed_number || "",
    notes: land?.notes || "", image_urls: land?.image_urls || "",
  });
  const [saving, setSaving] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  // حساب الإجمالي تلقائيًا
  useEffect(() => {
    const a = Number(f.area_sqm), p = Number(f.price_per_sqm);
    if (f.area_sqm !== "" && f.price_per_sqm !== "" && !isNaN(a) && !isNaN(p)) set("total_price", String(Math.round(a * p)));
  }, [f.area_sqm, f.price_per_sqm]);
  // عند بدء الالتقاط: تصغير النافذة حتى تُرى الخريطة
  useEffect(() => { if (pickingActive) setMinimized(true); else setMinimized(false); }, [pickingActive]);

  const pickPoint = () => startPick("point", ({ lat, lng }) => { set("lat", lat.toFixed(7)); set("lng", lng.toFixed(7)); toast.success("تم تحديد الموقع"); });
  const pickPolygon = () => startPick("polygon", ({ polygon, area_sqm, lat, lng }) => {
    setF(p => ({ ...p, polygon, area_sqm: p.area_sqm || String(area_sqm), lat: p.lat || lat.toFixed(7), lng: p.lng || lng.toFixed(7) }));
    toast.success(`رُسمت الحدود — المساحة التقريبية ${area_sqm.toLocaleString()} م²`);
  });
  const useMapCenter = () => { const c = mapRef.current?.getCenter(); if (c) { set("lat", c.lat.toFixed(7)); set("lng", c.lng.toFixed(7)); } };

  const save = async () => {
    if (!f.title.trim()) return toast.error("اسم الأرض مطلوب");
    if (f.lat === "" || f.lng === "") return toast.error("حدد موقع الأرض على الخريطة");
    setSaving(true);
    try {
      if (land?.id) await api.update(land.id, f); else await api.create(f);
      toast.success(land?.id ? "تم التحديث" : "تمت الإضافة");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    setSaving(false);
  };

  if (minimized) {
    return (
      <div style={{ position: "fixed", top: 70, right: 20, zIndex: 1200, background: "#fff", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", padding: "8px 14px", fontSize: 12.5, fontWeight: 700, color: "#0f766e", direction: "rtl" }}>
        📝 نموذج الأرض مُصغّر أثناء التحديد على الخريطة…
      </div>
    );
  }
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "flex-start", padding: 16 }} onClick={onClose}>
      <div dir="rtl" onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 20, width: 560, maxWidth: "96vw", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>{land?.id ? "✏️ تعديل أرض" : "➕ إضافة أرض معروضة"}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={{ gridColumn: "1 / -1" }}><label style={lbl}>اسم/عنوان الأرض *</label><input value={f.title} onChange={e => set("title", e.target.value)} placeholder="مثال: أرض سكنية — حي النرجس" style={{ ...inp, fontWeight: 700 }} autoFocus /></div>
          <div><label style={lbl}>المدينة</label><input value={f.city} onChange={e => set("city", e.target.value)} style={inp} /></div>
          <div><label style={lbl}>الحي</label><input value={f.district} onChange={e => set("district", e.target.value)} style={inp} /></div>

          {/* الموقع */}
          <div style={{ gridColumn: "1 / -1", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 10, padding: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <label style={{ ...lbl, marginBottom: 0, color: "#0f766e", fontSize: 11 }}>📍 الموقع على الخريطة *</label>
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" onClick={pickPoint} style={{ padding: "5px 10px", background: "#0f766e", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}><FiCrosshair size={11} /> تحديد بالنقر</button>
                <button type="button" onClick={pickPolygon} style={{ padding: "5px 10px", background: "#f59e0b", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>✏️ رسم الحدود</button>
                <button type="button" onClick={useMapCenter} title="استخدام مركز الخريطة الحالي" style={{ padding: "5px 10px", background: "#fff", color: "#334155", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>مركز الخريطة</button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div><label style={lbl}>خط العرض (Lat)</label><input value={f.lat} onChange={e => set("lat", e.target.value)} placeholder="24.7136" style={{ ...inp, direction: "ltr" }} /></div>
              <div><label style={lbl}>خط الطول (Lng)</label><input value={f.lng} onChange={e => set("lng", e.target.value)} placeholder="46.6753" style={{ ...inp, direction: "ltr" }} /></div>
            </div>
            {f.polygon && <div style={{ fontSize: 11, color: "#0f766e", marginTop: 6 }}>✅ حدود مرسومة ({(f.polygon.coordinates?.[0]?.length || 1) - 1} نقطة) <button type="button" onClick={() => set("polygon", null)} style={{ background: "none", border: "none", color: "#b91c1c", cursor: "pointer", fontFamily: "inherit", fontSize: 11 }}>✕ إزالة</button></div>}
          </div>

          {/* التفاصيل */}
          <div><label style={lbl}>المساحة (م²)</label><input type="number" min="0" value={f.area_sqm} onChange={e => set("area_sqm", e.target.value)} style={{ ...inp, direction: "ltr" }} /></div>
          <div><label style={lbl}>نوع الأرض</label>
            <select value={f.land_type} onChange={e => set("land_type", e.target.value)} style={inp}><option value="">— اختر —</option>{LAND_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
          <div><label style={lbl}>عدد الشوارع</label><input type="number" min="0" max="4" value={f.streets_count} onChange={e => set("streets_count", e.target.value)} style={{ ...inp, direction: "ltr" }} /></div>
          <div><label style={lbl}>عرض الشوارع (م)</label><input value={f.street_widths} onChange={e => set("street_widths", e.target.value)} placeholder="مثال: 20 ، 15" style={inp} /></div>
          <div><label style={lbl}>سعر المتر (ر.س)</label><input type="number" min="0" value={f.price_per_sqm} onChange={e => set("price_per_sqm", e.target.value)} style={{ ...inp, direction: "ltr" }} /></div>
          <div><label style={lbl}>السعر الإجمالي (ر.س)</label><input type="number" min="0" value={f.total_price} onChange={e => set("total_price", e.target.value)} style={{ ...inp, direction: "ltr", fontWeight: 700, color: "#0f766e" }} /></div>
          <div><label style={lbl}>رقم المخطط</label><input value={f.plan_number} onChange={e => set("plan_number", e.target.value)} style={inp} /></div>
          <div><label style={lbl}>رقم القطعة</label><input value={f.plot_number} onChange={e => set("plot_number", e.target.value)} style={inp} /></div>
          <div><label style={lbl}>رقم الصك</label><input value={f.deed_number} onChange={e => set("deed_number", e.target.value)} style={{ ...inp, direction: "ltr" }} /></div>
          <div><label style={lbl}>الحالة</label>
            <div style={{ display: "flex", gap: 4 }}>{STATUSES.map(s => { const c = STATUS_COLORS[s]; const on = f.status === s; return (
              <button key={s} type="button" onClick={() => set("status", s)} style={{ flex: 1, padding: "7px 4px", borderRadius: 8, fontSize: 11, cursor: "pointer", fontFamily: "inherit", border: on ? `2px solid ${c.color}` : "1px solid #e2e8f0", background: on ? c.bg : "#fff", color: on ? c.color : "#64748b", fontWeight: on ? 800 : 500 }}>{s}</button>
            ); })}</div></div>
          <div><label style={lbl}>المالك</label><input value={f.owner_name} onChange={e => set("owner_name", e.target.value)} style={inp} /></div>
          <div><label style={lbl}>جوال المالك</label><input value={f.owner_phone} onChange={e => set("owner_phone", e.target.value)} style={{ ...inp, direction: "ltr" }} /></div>
          <div><label style={lbl}>الوسيط</label><input value={f.broker_name} onChange={e => set("broker_name", e.target.value)} style={inp} /></div>
          <div><label style={lbl}>جوال الوسيط</label><input value={f.broker_phone} onChange={e => set("broker_phone", e.target.value)} style={{ ...inp, direction: "ltr" }} /></div>
          <div style={{ gridColumn: "1 / -1" }}><label style={lbl}>ملاحظات</label><textarea value={f.notes} onChange={e => set("notes", e.target.value)} rows={3} style={{ ...inp, resize: "vertical" }} /></div>
          <div style={{ gridColumn: "1 / -1" }}><label style={lbl}>روابط صور (سطر لكل رابط)</label><textarea value={f.image_urls} onChange={e => set("image_urls", e.target.value)} rows={2} style={{ ...inp, resize: "vertical", direction: "ltr", fontSize: 11 }} /></div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button onClick={save} disabled={saving} style={{ flex: 1, padding: 10, background: "#0f766e", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 700, cursor: saving ? "wait" : "pointer", fontFamily: "inherit", opacity: saving ? 0.6 : 1 }}>{saving ? "⏳ جاري الحفظ..." : "حفظ"}</button>
          <button onClick={onClose} style={{ padding: "10px 20px", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>إلغاء</button>
        </div>
      </div>
    </div>
  );
}

// ─── إعدادات الخريطة (مفتاح Mapbox — أدمن) ───
function MapSettingsModal({ cfg, onClose, onSaved }) {
  const [token, setToken] = useState(cfg.mapbox_token || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.setMapbox(token.trim()); toast.success(token.trim() ? "تم تفعيل Mapbox Satellite" : "تم الرجوع لـ Esri World Imagery"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    setBusy(false);
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div dir="rtl" onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 20, width: 520, maxWidth: "96vw", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>🛰 إعدادات خريطة الأراضي</h3>
        <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.7, marginBottom: 12 }}>
          الخريطة تعمل حاليًا بـ <b>{cfg.provider === "mapbox" ? "Mapbox Satellite" : "Esri World Imagery (مجاني بلا مفتاح)"}</b>.
          لتفعيل <b>Mapbox Satellite</b> (دقة أعلى) أدخل Access Token من حسابك في mapbox.com — اتركه فارغًا للرجوع إلى Esri.
        </div>
        <label style={lbl}>Mapbox Access Token (pk.…)</label>
        <input value={token} onChange={e => setToken(e.target.value)} placeholder="pk.eyJ1Ijoi…" style={{ ...inp, direction: "ltr", fontFamily: "monospace", fontSize: 12 }} />
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button onClick={save} disabled={busy} style={{ flex: 1, padding: 10, background: "#0f766e", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{busy ? "…" : "حفظ"}</button>
          <button onClick={onClose} style={{ padding: "10px 20px", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>إغلاق</button>
        </div>
      </div>
    </div>
  );
}
